// More → Notifications: per-device push opt-in and the three notification types.
import { useCallback, useEffect, useState } from "react";
import { Bell, BellOff, BellRing, Loader2, Smartphone, Send, ShieldAlert, Share } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useRegion } from "@/contexts/RegionContext";
import { isIOS, isStandalone } from "@/lib/pwa";
import { disablePush, enablePush, permissionState, pushStatus, pushSupported, sendTestPush, setPushPrefs } from "@/lib/push";
import { cn } from "@/lib/utils";

const TYPES = [
  { key: "assigned", label: "Email assigned to you", sub: "Any of the four inboxes — sent straight away. Tap opens the email." },
  { key: "approvals", label: "Approval waiting for you", sub: "The moment something lands in your approvals queue. Tap opens Today." },
  { key: "overdue", label: "Action overdue", sub: "One summary at 08:00 desk time with how many of your actions are overdue or due today." },
];

export default function Notifications() {
  const { currentRegion } = useRegion();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [perm, setPerm] = useState(permissionState());
  const supported = pushSupported();
  const iosNeedsInstall = isIOS() && !isStandalone();

  const load = useCallback(async () => {
    try { setStatus(await pushStatus()); } catch { setStatus({ subscribed: false }); }
    setPerm(permissionState());
  }, []);
  useEffect(() => { if (supported) load(); else setStatus({ subscribed: false }); }, [load, supported]);

  const on = !!status?.subscribed;
  const prefs = status?.subscription?.prefs || { assigned: true, approvals: true, overdue: true };

  const toggle = async (v) => {
    setBusy(true);
    try {
      if (v) { const sub = await enablePush({ region: currentRegion }); setStatus({ subscribed: true, subscription: sub }); toast.success("Notifications are on for this device"); }
      else { await disablePush(); setStatus({ subscribed: false }); toast.success("Notifications are off for this device"); }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setBusy(false); setPerm(permissionState()); }
  };
  const setPref = async (key, v) => {
    const next = { ...prefs, [key]: v };
    setStatus((s) => ({ ...s, subscription: { ...s.subscription, prefs: next } }));
    try { await setPushPrefs({ [key]: v }, currentRegion); } catch (e) { toast.error(e.response?.data?.detail || e.message); load(); }
  };
  const test = async () => {
    setBusy(true);
    try { await sendTestPush(); toast.success("Test sent — it should pop up on this device in a moment"); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  const state = !supported ? "unsupported" : perm === "denied" ? "blocked" : on ? "on" : "off";
  const STATE = {
    on: { label: "On", tone: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: BellRing },
    off: { label: "Off", tone: "bg-slate-50 text-slate-700 border-slate-200", icon: BellOff },
    blocked: { label: "Blocked in browser settings", tone: "bg-rose-50 text-rose-800 border-rose-200", icon: ShieldAlert },
    unsupported: { label: "Not supported here", tone: "bg-amber-50 text-amber-800 border-amber-200", icon: ShieldAlert },
  }[state];
  const StateIcon = STATE.icon;

  return (
    <div className="space-y-4 sf-fade-up" data-testid="notifications-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Notifications</h1>
        <p className="text-sm text-slate-600 mt-1">Per device — switch them on here on each phone or laptop you want tapped on the shoulder.</p>
      </div>

      <div className={cn("rounded-xl border p-4 flex items-center gap-3", STATE.tone)} data-testid="notifications-state">
        <StateIcon className="w-6 h-6 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[15px]" data-testid="notifications-state-label">{STATE.label}</div>
          <div className="text-[12px] opacity-80">
            {state === "blocked" ? "Allow notifications for this site in your browser or phone settings, then come back here."
              : state === "unsupported" ? (iosNeedsInstall ? "On iPhone the app must be added to the Home Screen first (iOS 16.4 or newer)." : "This browser can't receive web push.")
              : on ? "This device gets the notifications ticked below." : "Nothing is sent to this device."}
          </div>
        </div>
        {supported && state !== "blocked" && (
          busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Switch checked={on} onCheckedChange={toggle} className="scale-125" data-testid="notifications-master-switch" />
        )}
      </div>

      {iosNeedsInstall && (
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-[13px] text-sky-900 flex gap-3" data-testid="notifications-ios-hint">
          <Smartphone className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">iPhone: add to Home Screen first</div>
            <div className="mt-1">Tap <Share className="inline w-3.5 h-3.5 align-text-bottom" /> Share → <b>Add to Home Screen</b>, open SimplyFunded from there, then switch notifications on. Needs iOS 16.4 or newer.</div>
          </div>
        </div>
      )}

      <div className="rounded-xl border bg-white divide-y" data-testid="notifications-types">
        {TYPES.map((t) => (
          <label key={t.key} className={cn("flex items-start gap-3 p-4", !on && "opacity-60")} data-testid={`notifications-type-${t.key}`}>
            <Bell className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[15px]">{t.label}</div>
              <div className="text-[12px] text-slate-500 mt-0.5">{t.sub}</div>
            </div>
            <Switch checked={prefs[t.key] !== false} disabled={!on} onCheckedChange={(v) => setPref(t.key, v)} data-testid={`notifications-switch-${t.key}`} />
          </label>
        ))}
      </div>

      <div className="text-[12px] text-slate-500 space-y-1" data-testid="notifications-notes">
        <p>Bursts are grouped — several assignments become one updated banner. Nothing is sent for unassigned new mail; that stays a count badge.</p>
        <p>Signing out on a device switches its notifications off. Devices that stop responding are quietly forgotten.</p>
      </div>

      {on && (
        <Button variant="outline" className="h-11 w-full" onClick={test} disabled={busy} data-testid="notifications-test-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Send a test notification
        </Button>
      )}
    </div>
  );
}
